/**
 * Client-side image compressor for bukti-muat uploads.
 *
 * Scales the longest edge to `maxDim` px (keeping aspect ratio) and re-encodes
 * as JPEG, ratcheting quality down until the result is ≤ `targetKB` OR quality
 * hits `minQuality`. Keeps EXIF orientation via `imageOrientation:'from-image'`
 * so portrait phone photos don't come out sideways.
 *
 * Falls back to the original File when:
 *   - it isn't an image/*,
 *   - or it's already smaller than `targetKB`,
 *   - or the browser can't decode it (compression throws → return original,
 *     upload still succeeds, just uncompressed).
 */
import { devDebug } from "@/lib/debug";

const DEFAULTS = { maxDim: 1200, targetKB: 200, minQuality: 0.5 };

async function _drawScaled(file, maxDim) {
  // Prefer createImageBitmap — it handles EXIF orientation natively.
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const { width: w, height: h } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  // Fill white first so transparent PNGs (screenshots, alpha layers) don't
  // render as pitch-black areas after JPEG re-encoding.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, tw, th);
  ctx.drawImage(bitmap, 0, 0, tw, th);
  bitmap.close?.();
  return canvas;
}

function _canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
      "image/jpeg",
      quality,
    );
  });
}

export async function compressImage(file, opts = {}) {
  if (!file) return file;
  const { maxDim, targetKB, minQuality } = { ...DEFAULTS, ...opts };
  const targetBytes = targetKB * 1024;

  // Skip: not an image, or already small enough.
  if (!file.type?.startsWith("image/")) return file;
  if (file.size <= targetBytes && !file.type.includes("heic") && !file.type.includes("heif")) {
    return file;
  }

  try {
    const canvas = await _drawScaled(file, maxDim);
    let quality = 0.85;
    let blob = await _canvasToBlob(canvas, quality);
    while (blob.size > targetBytes && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.1);
      // eslint-disable-next-line no-await-in-loop
      blob = await _canvasToBlob(canvas, quality);
    }
    // If we somehow made it BIGGER (rare — small icons), keep original.
    if (blob.size >= file.size) return file;
    const newName = (file.name || "upload").replace(/\.(png|webp|heic|heif|bmp|jpe?g)$/i, "") + ".jpg";
    const compressed = new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
    devDebug(
      `[imageCompress] ${file.name} ${(file.size / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB @q${quality.toFixed(2)}`,
    );
    return compressed;
  } catch (err) {
    devDebug("[imageCompress] fallback to original:", err);
    return file;
  }
}
